// Arcturian Realtime-Wire v1 — Turn-Korrelation für den Browser.
//
// Quelle der Wahrheit: Product #4431 `p-5cde1ac88a89` (approved 2026-08-02) und
// der eingefrorene Wire aus Issue #886 / Collab-Task `t-3077cdefc69c`. Die
// Freigabe für diese Web-Lane ist scoped (AppDev, 2026-08-04): den BESTEHENDEN
// serverseitigen Resolver-/Receipt-Vertrag konsumieren — kein Web-Executor,
// keine zweite Authority-Domain, keine Client-Heuristik.
//
// WARUM ES DIESES MODUL GIBT: iOS hat denselben Wire zuerst mit globalen
// „pending"-Flags gebunden — die jeweils nächste `response.created` galt als die
// erwartete. Das bricht, sobald ein verspätetes oder abgebrochenes Event
// eintrifft: ein terminales Event einer ABGEBROCHENEN Response wurde dem
// nächsten Turn zugeschlagen und konnte ein Receipt für etwas prägen, das nie
// passiert ist (#886). Der Browser-Hook macht es heute noch genau so
// (`responseActiveRef` ist ein einzelnes Boolean) — deshalb ist die Korrelation
// hier ein eigener, vollständig testbarer Zustandsautomat statt Logik im Hook.
//
// Kernsatz aus dem Vertrag, an dem sich alles ausrichtet: plausibler Modelltext
// beweist keine Handlung. Dieses Modul entscheidet daher NIE, dass etwas
// ausgeführt wurde — es entscheidet ausschließlich, WELCHEM Turn ein Event
// gehört und ob es überhaupt verwertet werden darf.

/** Serverautorisierte Response-Art. Wird von AiApi in `response.metadata`
 *  gesetzt; der Client erfindet sie nie. */
export type ResponseKind =
  | 'arcturian.resolver'
  | 'arcturian.primary_audio'
  /** v3: der erzwungene Nachschlag vor dem Sprechzug. Adapter-gesetzt wie
   *  `primary_audio` — der Server liefert nur die Vorlage. */
  | 'arcturian.status_lookup'
  /** #1035: die eine Zeile, die nur das Modell kennt — was der Bediener
   *  wollte. Laeuft nur nach einem Fehlschlag, textlos, ohne Stimme. */
  | 'arcturian.report_intent'
  | 'agentos.affect'
  | 'plain';

const KINDS: readonly string[] = [
  'arcturian.resolver', 'arcturian.primary_audio', 'arcturian.status_lookup',
  'arcturian.report_intent', 'agentos.affect',
];

/** Metadata-Feldnamen sind eingefroren (#886, „Mint-Feldnamen eingefroren").
 *  `agentos_response_kind` ist servergesetzt; `agentos_turn_id` und
 *  `agentos_source_response_id` setzt der Adapter. */
export const META_KIND = 'agentos_response_kind';
export const META_TURN = 'agentos_turn_id';
export const META_SOURCE = 'agentos_source_response_id';

export interface ResponseMeta {
  [META_KIND]?: unknown;
  [META_TURN]?: unknown;
  [META_SOURCE]?: unknown;
}

/** Ein registrierter Turn-Slot. `cancelling` ist der Zustand zwischen unserem
 *  `response.cancel` und dem terminalen `response.done` — in diesem Fenster
 *  sind Function-Calls wirkungslos. */
export interface Registration {
  responseId: string;
  kind: ResponseKind;
  turnId?: string;
  sourceResponseId?: string;
  cancelling: boolean;
  terminal: boolean;
  cancelRequestedAt?: number;
}

/** Warum ein Event verworfen wurde — für sichtbares Verwerfen statt stillem
 *  Schlucken. Ein unerklärt verschwundenes Event ist im Betrieb schlimmer als
 *  ein abgelehntes. */
export type DropReason =
  | 'missing_metadata'
  | 'unknown_kind'
  | 'unknown_response_id'
  | 'duplicate_response_id'
  | 'turn_rebind'
  | 'metadata_mismatch'
  | 'response_cancelling'
  | 'duplicate_call_id'
  | 'already_terminal'
  | 'unexpected_response';

export type Verdict =
  | { accepted: true; registration: Registration }
  | { accepted: false; reason: DropReason; detail?: string };

const ok = (registration: Registration): Verdict => ({ accepted: true, registration });
const drop = (reason: DropReason, detail?: string): Verdict => ({ accepted: false, reason, detail });

function readMeta(meta: ResponseMeta | undefined | null): { kind?: ResponseKind; turnId?: string; source?: string } {
  if (!meta || typeof meta !== 'object') return {};
  const rawKind = meta[META_KIND];
  const kind = typeof rawKind === 'string' && KINDS.includes(rawKind) ? (rawKind as ResponseKind) : undefined;
  const turnId = typeof meta[META_TURN] === 'string' ? (meta[META_TURN] as string) : undefined;
  const source = typeof meta[META_SOURCE] === 'string' ? (meta[META_SOURCE] as string) : undefined;
  return { kind, turnId, source };
}

/**
 * Zustandsautomat für die Response-Korrelation einer Arcturian-Session.
 *
 * Fail-closed by design: was nicht eindeutig einem registrierten Turn zugeordnet
 * werden kann, wird verworfen — nie geraten. Jede Ablehnung nennt einen Grund,
 * damit die Oberfläche sie sichtbar machen kann.
 */
export interface WireOptions {
  /**
   * Verlangt servergesetzte `agentos_response_kind`-Metadata (Arcturian).
   * Für Narrator-/Talkback-Sessions FALSE: dort setzt AiApi keine Metadata,
   * die Response entsteht durch ein clientseitiges `response.create`. Die
   * response_id-Bindung — der eigentliche Schutz — gilt in beiden Fällen
   * unverändert. Default true, damit der strengere Modus der Default bleibt.
   */
  requireKind?: boolean;
  /** Also require that the exact kind/turn/source identity was registered
   * before response.create was sent. Used by Arcturian; legacy modes keep the
   * historical response-id-only behavior. */
  requireExpected?: boolean;
}

export class ArcturianWire {
  private byResponse = new Map<string, Registration>();
  private turnBinding = new Map<string, string>();
  private expected = new Set<string>();
  private seenCallIds = new Set<string>();
  private requireKind: boolean;
  private requireExpected: boolean;

  constructor(opts: WireOptions = {}) {
    this.requireKind = opts.requireKind !== false;
    this.requireExpected = opts.requireExpected === true;
  }

  private identity(kind: ResponseKind, turnId?: string, source?: string): string {
    return `${kind}\u0000${turnId || ''}\u0000${source || ''}`;
  }

  /** Register the exact identity immediately before emitting response.create. */
  expect(kind: Exclude<ResponseKind, 'plain'>, turnId: string, sourceResponseId?: string): void {
    if (!turnId) throw new Error('agentos_turn_id fehlt');
    this.expected.add(this.identity(kind, turnId, sourceResponseId));
  }

  /** `response.created` — der einzige Ort, an dem ein Turn-Slot entsteht. */
  responseCreated(responseId: string, meta: ResponseMeta | null | undefined): Verdict {
    if (!responseId) return drop('missing_metadata', 'response.id fehlt');
    if (this.byResponse.has(responseId)) return drop('duplicate_response_id', responseId);

    const { kind: metaKind, turnId, source } = readMeta(meta);
    let kind: ResponseKind;
    if (this.requireKind) {
      // Fehlende oder fremde Metadata ist fail-closed. Ein Default wie „das wird
      // schon primary_audio sein" ist exakt die Heuristik, die der Vertrag
      // verbietet — und die in #886 das falsche Receipt erzeugt hat.
      if (!meta || meta[META_KIND] === undefined) return drop('missing_metadata', responseId);
      if (!metaKind) return drop('unknown_kind', String(meta[META_KIND]));
      kind = metaKind;
    } else {
      // Narrator/Talkback: keine Kind-Metadata im Spiel. Trotzdem registrieren —
      // die Bindung an die response_id ist hier der ganze Zweck.
      kind = metaKind ?? 'plain';
    }

    const identity = this.identity(kind, turnId, source);
    if (this.requireExpected && !this.expected.has(identity)) {
      return drop('unexpected_response', identity);
    }
    this.expected.delete(identity);

    // Eine Response-Identität (Art + Turn + optionale Quelle) wird genau einmal
    // gebunden. Resolver und Primary teilen absichtlich dieselbe Turn-ID; nur
    // die vollständige Identität darf deshalb der Schlüssel sein.
    if (turnId) {
      const bound = this.turnBinding.get(identity);
      if (bound && bound !== responseId) return drop('turn_rebind', `${identity} → ${bound}`);
      this.turnBinding.set(identity, responseId);
    }

    const reg: Registration = { responseId, kind, turnId, sourceResponseId: source, cancelling: false, terminal: false };
    this.byResponse.set(responseId, reg);
    return ok(reg);
  }

  /** Unser eigenes `response.cancel` — ab hier ist der Slot gesperrt. Der
   *  Serverzustand ist damit NICHT terminal; das entscheidet erst
   *  `response.done`. */
  markCancelling(responseId: string, now = Date.now()): Verdict {
    const reg = this.byResponse.get(responseId);
    if (!reg) return drop('unknown_response_id', responseId);
    reg.cancelling = true;
    reg.cancelRequestedAt = now;
    return ok(reg);
  }

  /**
   * `response.done` — die primäre und einzige Terminalquelle
   * (`response.done.response.id` + `response.status`). Die Metadata muss
   * bitgleich zu der aus `response.created` sein; Abweichung ist fail-closed.
   */
  responseDone(responseId: string, meta: ResponseMeta | null | undefined, _status?: string): Verdict {
    const reg = this.byResponse.get(responseId);
    if (!reg) return drop('unknown_response_id', responseId);
    if (reg.terminal) return drop('already_terminal', responseId);

    if (this.requireKind) {
      const { kind, turnId, source } = readMeta(meta);
      if (kind !== reg.kind || turnId !== reg.turnId || source !== reg.sourceResponseId) {
        return drop('metadata_mismatch', `${reg.kind}/${reg.turnId ?? '-'} ≠ ${kind ?? '-'}/${turnId ?? '-'}`);
      }
    }
    reg.terminal = true;
    reg.cancelling = false;
    return ok(reg);
  }

  /** Top-level `response.cancelled` / `response.incomplete` — laut Wire NUR
   *  Alias-Fallback. Sie markieren den Slot, sind aber ausdrücklich kein
   *  Ersatz für ein terminales `response.done`. */
  aliasTerminal(responseId: string): Verdict {
    const reg = this.byResponse.get(responseId);
    if (!reg) return drop('unknown_response_id', responseId);
    reg.cancelling = true;
    return ok(reg);
  }

  /**
   * Ein Function-Call (aus `response.function_call_arguments.done` ODER als
   * Fallback aus `response.done.output`). Zwei Sperren:
   *  1. Der Slot muss existieren — Bindung ausschließlich über response_id.
   *  2. Ist der Slot `cancelling`, wird verworfen: kein Executor, kein Receipt,
   *     kein ACK. Auch bei vollständig wohlgeformten Argumenten — genau dieser
   *     adversariale Fall ist in #886 als Regression gepinnt.
   * `call_id` wird dedupliziert, weil derselbe Call über beide Wege eintrifft.
   */
  functionCall(responseId: string, callId: string): Verdict {
    const reg = this.byResponse.get(responseId);
    if (!reg) return drop('unknown_response_id', responseId);
    if (reg.terminal) return drop('already_terminal', callId);
    if (reg.cancelling) return drop('response_cancelling', callId);
    if (callId && this.seenCallIds.has(callId)) return drop('duplicate_call_id', callId);
    if (callId) this.seenCallIds.add(callId);
    return ok(reg);
  }

  /** Audio-Deltas werden ebenfalls nur über die response_id gebunden — ein
   *  verspätetes Audio-Event einer alten Response darf nie den neuen Turn
   *  bespielen. */
  audioDelta(responseId: string): Verdict {
    const reg = this.byResponse.get(responseId);
    if (!reg) return drop('unknown_response_id', responseId);
    if (reg.cancelling) return drop('response_cancelling', responseId);
    return ok(reg);
  }

  /**
   * `response_cancel_not_active` ist benign (der Server kannte die Response
   * nicht mehr) und darf NICHT als abgeschlossenes Serverterminal gelten.
   * Bleibt das terminale `response.done` aus, wird der Slot nach einer
   * begrenzten Frist SICHTBAR verworfen — niemals ungeprüft weitergefahren.
   * Rückgabe: die verworfenen Slots, damit die Oberfläche es anzeigen kann.
   */
  sweepStaleCancels(timeoutMs: number, now = Date.now()): Registration[] {
    const stale: Registration[] = [];
    for (const reg of this.byResponse.values()) {
      if (reg.terminal || !reg.cancelling || reg.cancelRequestedAt == null) continue;
      if (now - reg.cancelRequestedAt >= timeoutMs) {
        stale.push(reg);
        this.byResponse.delete(reg.responseId);
        if (reg.turnId) this.turnBinding.delete(this.identity(reg.kind, reg.turnId, reg.sourceResponseId));
      }
    }
    return stale;
  }

  /** Slot bedingungslos verwerfen — für den Watchdog-Fall, in dem ein
   *  terminales `response.done` ausbleibt. Bewusst getrennt von
   *  `sweepStaleCancels`: dort geht es um abgebrochene Responses, hier um eine,
   *  die nie terminal wurde. Gibt den verworfenen Slot zurück, damit der
   *  Aufrufer ihn sichtbar machen kann. */
  discard(responseId: string): Registration | undefined {
    const reg = this.byResponse.get(responseId);
    if (!reg) return undefined;
    this.byResponse.delete(responseId);
    if (reg.turnId) this.turnBinding.delete(this.identity(reg.kind, reg.turnId, reg.sourceResponseId));
    return reg;
  }

  get(responseId: string): Registration | undefined {
    return this.byResponse.get(responseId);
  }

  /** Nur für Tests/Diagnose — offene, nicht-terminale Slots. */
  openSlots(): Registration[] {
    return [...this.byResponse.values()].filter((r) => !r.terminal);
  }
}

// ---------------------------------------------------------------------------
// Mint-Contract-Verifikation
//
// Der Mint liefert den Vertrag SELBST mit — AiApi schickt bei
// `companion_mode: "arcturian"` neben dem Token auch `response_kind_field`,
// `response_kinds` (server_set/adapter_set/adapter_correlation_keys) und das
// fertige `primary_audio_response`-Template. Der Servercode sagt dazu wörtlich,
// es werde ausgeliefert, „so it can VALIDATE against the measured contract
// instead of re-deriving it".
//
// Deshalb prüft der Client hier gegen das, was er selbst implementiert, und
// verbindet sich NICHT, wenn etwas abweicht. Das ist bewusst das Gegenteil von
// „nimm, was der Server schickt": weicht der Vertrag ab, ist unsere
// Korrelationslogik nicht mehr die, die gemessen wurde — dann ist Abbruch die
// richtige Antwort. iOS meldet denselben Fall als `invalidTokenContract`.
// ---------------------------------------------------------------------------

export interface MintContract {
  companion_mode?: unknown;
  tools?: unknown;
  arcturian_resolver?: unknown;
  response_kind_field?: unknown;
  response_kinds?: {
    server_set?: unknown;
    adapter_set?: unknown;
    adapter_correlation_keys?: unknown;
  } | null;
  primary_audio_response?: {
    type?: unknown;
    response?: { tools?: unknown; tool_choice?: unknown; output_modalities?: unknown; metadata?: ResponseMeta };
  } | null;
  resolver_followup?: {
    type?: unknown;
    response?: { tools?: unknown; tool_choice?: unknown; output_modalities?: unknown; metadata?: ResponseMeta };
  } | null;
  affect_followup?: {
    type?: unknown;
    response?: { tools?: unknown; tool_choice?: unknown; output_modalities?: unknown; metadata?: ResponseMeta };
  } | null;
  /** #1035: erzwungener Zug fuer die Absichtszeile nach einem Fehlschlag. */
  report_intent_response?: {
    type?: unknown;
    response?: { tools?: unknown; tool_choice?: unknown; output_modalities?: unknown; metadata?: ResponseMeta };
  } | null;
  /** v3: erzwungener Nachschlag vor dem Sprechzug. Fehlt ohne `read_tools`. */
  status_lookup_response?: {
    type?: unknown;
    response?: { tools?: unknown; tool_choice?: unknown; output_modalities?: unknown; metadata?: ResponseMeta };
  } | null;
}

export type ContractVerdict = { ok: true } | { ok: false; reason: string };

const sameSet = (a: unknown, b: readonly string[]) =>
  Array.isArray(a) && a.length === b.length && b.every((x) => a.includes(x));

/**
 * Die Adapter-Menge darf wachsen, die Server-Menge nicht.
 *
 * Vorher verglich diese Prüfung die Adapter-Menge auf Gleichheit mit
 * `['arcturian.primary_audio']`. Als AiApi am 11.08. zwei Vorlagen ergänzte
 * (`arcturian.report_intent`, `arcturian.status_lookup`), scheiterte damit
 * JEDE Arcturian-Sitzung vor dem Mikrofon — mit einer Meldung über eine
 * unerwartete Aufzählung, obwohl nichts Unerwartetes passiert war.
 *
 * Der Fehler lag in der Prüfung, nicht im Mint: Adapter-Kinds sind die, die
 * ICH auf die Leitung lege. Kommt eine Vorlage dazu, die ich nicht kenne,
 * benutze ich sie einfach nicht — das ist folgenlos. Genau dieselbe Toleranz
 * gilt schon für die Lesewerkzeuge ein paar Zeilen weiter unten.
 *
 * Schützenswert ist die TRENNUNG, nicht der Umfang: Ein serverautorisierter
 * Kind darf nie in der Adapter-Menge auftauchen, sonst könnte ich einen
 * selbst gesetzten Typ für serverbestätigt halten. Deshalb bleibt die
 * Server-Menge exakt gepinnt und die Adapter-Menge muss zu ihr disjunkt sein.
 */
const containsAllDisjoint = (a: unknown, required: readonly string[], forbidden: readonly string[]) =>
  Array.isArray(a)
  && a.every((x) => typeof x === 'string')
  && required.every((x) => a.includes(x))
  && !a.some((x) => forbidden.includes(x as string));

const ARCTURIAN_REQUIRED_READ_TOOL = 'agent_status';
const ARCTURIAN_FORBIDDEN_PRIMARY_TOOLS = new Set([
  'resolve_arcturian_turn',
  'report_affect',
]);

function stringToolNames(tools: unknown): string[] | null {
  if (!Array.isArray(tools) || tools.some((tool) => typeof tool !== 'string')) return null;
  return tools as string[];
}

function definedToolNames(tools: unknown): string[] | null {
  if (!Array.isArray(tools)) return null;
  const names = tools.map((tool) => (
    tool && typeof tool === 'object' && !Array.isArray(tool)
      && (tool as Record<string, unknown>).type === 'function'
      ? (tool as Record<string, unknown>).name
      : undefined
  ));
  return names.every((name) => typeof name === 'string') ? names as string[] : null;
}

function readToolContractIsSafe(names: string[] | null): boolean {
  return Boolean(names?.includes(ARCTURIAN_REQUIRED_READ_TOOL)
    && !names.some((name) => ARCTURIAN_FORBIDDEN_PRIMARY_TOOLS.has(name)));
}

/**
 * Prüft einen Arcturian-Mint gegen den Vertrag, den dieser Client implementiert.
 * Fail-closed: jede Abweichung ist ein Grund, nicht zu verbinden.
 */
export function verifyArcturianMint(mint: MintContract | null | undefined): ContractVerdict {
  if (!mint || typeof mint !== 'object') return { ok: false, reason: 'kein Mint-Payload' };
  if (mint.companion_mode !== 'arcturian') {
    return { ok: false, reason: `companion_mode ${String(mint.companion_mode)} ≠ arcturian` };
  }
  if (mint.response_kind_field !== META_KIND) {
    return { ok: false, reason: `response_kind_field ${String(mint.response_kind_field)} ≠ ${META_KIND}` };
  }
  // v3 (AiApi cef628d) ergänzt `query_status` — die Statusfrage als eigene
  // Entscheidung, damit der Nachschlag ERZWUNGEN statt erhofft ist (gemessen
  // 8/8 gegen 1/8). Die Liste bleibt eine Aufzählung und keine offene Tür:
  // Eine unbekannte Fassung könnte Entscheidungen tragen, die dieser Client
  // nicht ausführen kann — DAS ist hier das Schützenswerte, anders als bei der
  // Adapter-Menge, wo eine unbekannte Vorlage einfach ungenutzt bleibt.
  const KNOWN_RESOLVERS = [
    'agentos.arcturian-action.v1',
    'agentos.arcturian-action.v2',
    'agentos.arcturian-action.v3',
  ];
  if (typeof mint.arcturian_resolver !== 'string' || !KNOWN_RESOLVERS.includes(mint.arcturian_resolver)) {
    return { ok: false, reason: `arcturian_resolver unerwartet: ${String(mint.arcturian_resolver)}` };
  }
  // Owner-approved read capability (AiApi 0d421b5): require the capability
  // this client implements, but do not pin the whole read-only list. AiApi may
  // add another read tool without breaking session startup. Action and affect
  // tools remain forced response-level capabilities and are always forbidden.
  const sessionToolNames = stringToolNames(mint.tools);
  if (!readToolContractIsSafe(sessionToolNames)) {
    return { ok: false, reason: `Session-Tools unerwartet: ${JSON.stringify(mint.tools)}` };
  }

  const kinds = mint.response_kinds;
  if (!kinds) return { ok: false, reason: 'response_kinds fehlt' };
  // Server setzt resolver + affect, der Adapter setzt primary_audio. Diese
  // Trennung ist Vertrag, nicht Zufall — wer sie verwischt, könnte einen
  // adaptergesetzten Kind für serverautorisiert halten.
  if (!sameSet(kinds.server_set, ['arcturian.resolver', 'agentos.affect'])) {
    return { ok: false, reason: `server_set unerwartet: ${JSON.stringify(kinds.server_set)}` };
  }
  if (!containsAllDisjoint(
    kinds.adapter_set,
    ['arcturian.primary_audio'],
    ['arcturian.resolver', 'agentos.affect'],
  )) {
    return { ok: false, reason: `adapter_set unerwartet: ${JSON.stringify(kinds.adapter_set)}` };
  }
  if (!sameSet(kinds.adapter_correlation_keys, [META_TURN, META_SOURCE])) {
    return { ok: false, reason: `correlation_keys unerwartet: ${JSON.stringify(kinds.adapter_correlation_keys)}` };
  }

  // The spoken turn must explicitly expose the approved read capability. An
  // omitted tools field would inherit session capabilities; forbidden action
  // or affect tools would re-open the misrouting fixed by #886.
  const primary = mint.primary_audio_response?.response;
  if (mint.primary_audio_response?.type !== 'response.create' || !primary) {
    return { ok: false, reason: 'primary_audio_response fehlt' };
  }
  const primaryToolNames = definedToolNames(primary.tools);
  if (!readToolContractIsSafe(primaryToolNames)) {
    return { ok: false, reason: `primary.tools unerwartet: ${JSON.stringify(primary.tools)}` };
  }
  if (primary.tool_choice !== 'auto') {
    return { ok: false, reason: `primary.tool_choice ${String(primary.tool_choice)} ≠ auto` };
  }
  if (primary.metadata?.[META_KIND] !== 'arcturian.primary_audio') {
    return { ok: false, reason: `primary metadata kind ${String(primary.metadata?.[META_KIND])}` };
  }

  const resolver = mint.resolver_followup?.response;
  const resolverTools = Array.isArray(resolver?.tools) ? resolver.tools : [];
  const resolverTool = resolverTools[0] as Record<string, unknown> | undefined;
  if (mint.resolver_followup?.type !== 'response.create'
      || !resolver
      || resolver.tool_choice !== 'required'
      || JSON.stringify(resolver.output_modalities) !== JSON.stringify(['text'])
      || resolverTools.length !== 1
      || resolverTool?.type !== 'function'
      || resolverTool?.name !== 'resolve_arcturian_turn'
      || resolver.metadata?.[META_KIND] !== 'arcturian.resolver') {
    return { ok: false, reason: 'resolver_followup ungültig' };
  }

  // Nur prüfen, wenn geliefert: ohne `read_tools` gibt es die Vorlage nicht,
  // und eine fehlende Fähigkeit ist kein Vertragsbruch. Ist sie aber da, muss
  // sie stimmen — `tool_choice: required` ist ihr ganzer Zweck. Genau daran
  // scheiterte der alte Weg: erlaubt statt verlangt heißt in 7 von 8 Fällen
  // „einen Moment", und dann nichts.
  if (mint.status_lookup_response != null) {
    const lookup = mint.status_lookup_response.response;
    const lookupTools = definedToolNames(lookup?.tools);
    if (mint.status_lookup_response.type !== 'response.create'
        || !lookup
        || lookup.tool_choice !== 'required'
        || !lookupTools
        || !lookupTools.includes(ARCTURIAN_REQUIRED_READ_TOOL)
        || !readToolContractIsSafe(lookupTools)
        || lookup.metadata?.[META_KIND] !== 'arcturian.status_lookup') {
      return { ok: false, reason: 'status_lookup_response ungültig' };
    }
  }

  // Auch hier nur pruefen, wenn geliefert. `required` ist wieder der Kern:
  // AiApi mass die erzwungene Form 6/6, die benannte Funktionsform 0/6. Und
  // `output_modalities: ['text']` ist keine Sparsamkeit, sondern Vertrag — die
  // Erklaerung gehoert in den regulaeren Sprechzug, nicht hierher.
  if (mint.report_intent_response != null) {
    const intent = mint.report_intent_response.response;
    const intentTools = Array.isArray(intent?.tools) ? intent.tools : [];
    const intentTool = intentTools[0] as Record<string, unknown> | undefined;
    if (mint.report_intent_response.type !== 'response.create'
        || !intent
        || intent.tool_choice !== 'required'
        || JSON.stringify(intent.output_modalities) !== JSON.stringify(['text'])
        || intentTools.length !== 1
        || intentTool?.type !== 'function'
        || intentTool?.name !== 'report_intent'
        || intent.metadata?.[META_KIND] !== 'arcturian.report_intent') {
      return { ok: false, reason: 'report_intent_response ungültig' };
    }
  }

  if (mint.affect_followup != null) {
    const affect = mint.affect_followup.response;
    const affectTools = Array.isArray(affect?.tools) ? affect.tools : [];
    const affectTool = affectTools[0] as Record<string, unknown> | undefined;
    if (mint.affect_followup.type !== 'response.create'
        || !affect
        || affect.tool_choice !== 'required'
        || JSON.stringify(affect.output_modalities) !== JSON.stringify(['text'])
        || affectTools.length !== 1
        || affectTool?.type !== 'function'
        || affectTool?.name !== 'report_affect'
        || affect.metadata?.[META_KIND] !== 'agentos.affect') {
      return { ok: false, reason: 'affect_followup ungültig' };
    }
  }

  return { ok: true };
}

export interface ResponseCreateTemplate {
  type: 'response.create';
  response: Record<string, unknown> & { metadata?: ResponseMeta };
}

export const MAX_ARCTURIAN_READ_CALLS_PER_TURN = 2;

export function shouldSealArcturianReadTools(readToolCallCount: number): boolean {
  return readToolCallCount >= MAX_ARCTURIAN_READ_CALLS_PER_TURN;
}

/** Close the read-tool gate after the bounded lookup budget is exhausted.
 * All other server-owned response fields remain equivalent; adapter
 * correlation metadata is still added by prepareArcturianResponse. */
export function sealArcturianReadTools<T extends ResponseCreateTemplate>(
  template: T,
): T {
  return {
    ...template,
    response: {
      ...template.response,
      tools: [],
      tool_choice: 'none',
      ...(template.response.metadata
        ? { metadata: { ...template.response.metadata } }
        : {}),
    },
  } as T;
}

/** Copy a server-provided response.create template and add only adapter-owned
 * correlation metadata. Tool lists, instructions and tool_choice stay exactly
 * as minted. */
export function prepareArcturianResponse(
  template: ResponseCreateTemplate,
  kind: Exclude<ResponseKind, 'plain'>,
  turnId: string,
  sourceResponseId?: string,
): ResponseCreateTemplate {
  if (!template || template.type !== 'response.create' || !template.response || !turnId) {
    throw new Error('invalid_arcturian_response_template');
  }
  const serverKind = template.response.metadata?.[META_KIND];
  if (serverKind !== kind) throw new Error('invalid_arcturian_response_kind');
  return {
    ...template,
    response: {
      ...template.response,
      metadata: {
        ...(template.response.metadata || {}),
        [META_TURN]: turnId,
        ...(sourceResponseId ? { [META_SOURCE]: sourceResponseId } : {}),
      },
    },
  };
}

/**
 * Darf die Oberfläche „ausgeführt" anzeigen?
 *
 * Der Vertrag lässt hier keinen Spielraum: ausschließlich aus einem
 * gespeicherten terminalen Action-Event/Receipt, nie aus Modelltext. Diese
 * Funktion nimmt deshalb bewusst KEINEN Text und keine Modellausgabe entgegen —
 * sie sieht nur, was der Server persistiert hat.
 */
export function rendersAsExecuted(actionEvent: { status?: unknown; action_id?: unknown } | null | undefined): boolean {
  if (!actionEvent || typeof actionEvent !== 'object') return false;
  if (typeof actionEvent.action_id !== 'string' || !actionEvent.action_id) return false;
  return actionEvent.status === 'completed';
}
